
import Vue, { PropType } from 'vue';
import { mapActions } from 'vuex';
import { RunnitNodeField, RUNNIT_NODE_FIELD_TYPE, RunnitNodeRunInputValue } from '@run-diffusion/shared';
import MenuOrBottomSheet from '@/components/base/MenuOrBottomSheet.vue';
import AspectRatioSVG from '@/assets/AspectRatioSVG.vue';
import { getLogicOnFieldsFromField } from '@run-diffusion/shared';
import _isNil from 'lodash/isNil';
import { SNACKBAR_STATUS } from '@/constants/constants';
import { dataSourceDimensions } from '@/views/Runnits/RunnitSettings/WidthHeightField/utils';

export default Vue.extend({
	name: 'ChipField',
	props: {
		field: {
			type: Object as PropType<RunnitNodeField>,
			required: true,
		},
		value: {
			type: [String, Number, Object],
			required: false,
		},
		inputValues: {
			type: Object as PropType<RunnitNodeRunInputValue>,
			required: true,
		},
	},
	data () {
		return {
			RUNNIT_NODE_FIELD_TYPE,
		};
	},
	computed: {
		logicOnFields () {
			return getLogicOnFieldsFromField(this.field, this.inputValues);
		},
		autoSetValue () {
			return this.logicOnFields.autoSetValue;
		},
		autoSetError () {
			return this.logicOnFields.autoSetError;
		},
		dataInputValue () {
			// Check for new unified field first, then fall back to legacy image field
			const dataFieldDefUuid = this._get(this.field, 'display.dataFieldDefUuid');
			if (dataFieldDefUuid) {
				return this.inputValues[dataFieldDefUuid];
			}

			// Legacy support for imageFieldDefUuid only (videoInputValue is new and can use dataFieldDefUuid)
			const imageFieldDefUuid = this._get(this.field, 'display.imageFieldDefUuid');
			if (imageFieldDefUuid) {
				return this.inputValues[imageFieldDefUuid];
			}

			return null;
		},
		// Legacy computed property for backward compatibility
		imageInputValue () {
			return this.dataInputValue;
		},
		dataInputDimensions () {
			return dataSourceDimensions(this.dataInputValue);
		},
		hasInputValue () {
			return !!this.dataInputDimensions;
		},
	},
	watch: {
		autoSetValue: {
			handler (newValue: any, oldValue: any) {
				if (_isNil(newValue) || newValue === oldValue) return;

				const options = this._get(this.field, 'display.select.options') || [];

				// Accept either string value or {width,height}
				if (typeof newValue === 'string') {
					const match = options.find((opt: any) => opt && opt.value === newValue);
					if (match) {
						this.onOptionClick(match, () => { });
						return;
					}
				} else {
					const w = this._get(newValue, 'width');
					const h = this._get(newValue, 'height');
					if (w && h) {
						const match = options.find((opt: any) => opt && opt.width === w && opt.height === h);
						if (match) {
							this.onOptionClick(match, () => { });
							return;
						}
					}
				}
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: this.autoSetError || 'Auto-set width/height did not match an available option.',
					show: true,
				});
			},
			immediate: true,
		},
		autoSetError: {
			handler (newVal: string, oldVal: string) {
				if (newVal && newVal !== oldVal) {
					this.updateSnackbar({
						status: SNACKBAR_STATUS.ERROR,
						message: newVal,
						show: true,
					});
				}
			},
			immediate: true,
		},
	},
	methods: {
		...mapActions([
			'updateSnackbar',
		]),
		isUltra (width: number, height: number): boolean {
			return width * height >= 1200000
		},
		onOptionClick (option: { width: number, height: number, value: string }, close: Function) {
			if (this._get(this.field, 'display.select.format') === 'value') {
				this.$emit('input', option.value);
			} else {
				this.$emit('input', { width: option.width, height: option.height });
			}
			if (close) {
				close();
			}
		},
		setWidthHeightFromImage (close: Function) {
			if (!this.dataInputDimensions) return;
			const { width, height } = this.dataInputDimensions;

			// Calculate the aspect ratio of the input
			const inputAspectRatio = width / height;

			// First pass: find the smallest aspect ratio difference
			let smallestAspectRatioDiff = Infinity;
			this._get(this.field, 'display.select.options').forEach((option) => {
				const optionAspectRatio = option.width / option.height;
				const diff = Math.abs(optionAspectRatio - inputAspectRatio);
				if (diff < smallestAspectRatioDiff) {
					smallestAspectRatioDiff = diff;
				}
			});

			// Second pass: among options with the closest aspect ratio, find the one with closest dimensions
			let closestOption = this._get(this.field, 'display.select.options')[0];
			let smallestDimensionDiff = Infinity;
			this._get(this.field, 'display.select.options').forEach((option) => {
				const optionAspectRatio = option.width / option.height;
				const aspectRatioDiff = Math.abs(optionAspectRatio - inputAspectRatio);
				
				// Only consider options with the closest aspect ratio
				if (Math.abs(aspectRatioDiff - smallestAspectRatioDiff) < 0.0001) {
					const diffWidth = Math.abs(option.width - width);
					const diffHeight = Math.abs(option.height - height);
					const dimensionDiff = diffWidth + diffHeight;
					
					if (dimensionDiff < smallestDimensionDiff) {
						closestOption = option;
						smallestDimensionDiff = dimensionDiff;
					}
				}
			});

			this.onOptionClick(closestOption, close);
		},
	},
	components: {
		MenuOrBottomSheet,
		AspectRatioSVG,
	},
});
